import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import {
  SYMBOL_CATEGORIES,
  TRADING_SYMBOLS,
} from "@/data/symbolsTradingHours";
import type { SymbolCategory, TradingSymbol } from "@/types/tradingTool";

const TABLE_HEAD =
  "whitespace-nowrap px-4 py-3 font-neue text-[10px] font-medium uppercase tracking-[0.12em] text-white sm:text-xs";

const TABLE_CELL =
  "whitespace-nowrap px-4 py-3.5 font-neue text-sm text-white/90";

function CategoryTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  if (active) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="btn-gradient-left shrink-0 rounded-md px-4 py-1.5 font-neue text-sm font-medium text-white "
      >
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-full px-5 py-2 font-neue text-sm text-white/45 transition-colors hover:text-white/70 md:text-base"
    >
      {label}
    </button>
  );
}

function SymbolFlag({ flagCode }: { flagCode: string }) {
  return (
    <img
      src={`https://flagcdn.com/24x18/${flagCode}.png`}
      alt="trading symbol flag"
      className="h-4.5 w-5 rounded-[2px] object-cover"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

function MarketStatusBadge({ symbol }: { symbol: TradingSymbol }) {
  const { t } = useTranslation();

  if (!symbol.isOpen) {
    return (
      <span className="rounded-md border border-white/10 bg-white/5 px-2.5 py-1 font-neue text-[11px] text-white/50 sm:text-xs">
        {t("Closed")}
      </span>
    );
  }

  return (
    <span className="rounded-md border border-[#9ED473]/20 bg-[#9ED473]/10 px-2.5 py-1.5 font-neue text-[11px] text-[#99FF48] sm:text-xs">
      {symbol.closesAt
        ? t("Open · closes {{time}}", { time: symbol.closesAt })
        : t("Open")}
    </span>
  );
}

function StatPill({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-transparent px-2.5 py-1 font-neue text-sm text-white/85">
      <span className="text-white/60">{label}</span>
      <span className="font-medium text-white">{value}</span>
    </span>
  );
}

function SymbolSpecBadges({ symbol }: { symbol: TradingSymbol }) {
  const { t } = useTranslation();
  const specs = [
    { label: t("Contract size"), value: symbol.contractSize },
    { label: t("Margin"), value: symbol.margin },
    { label: t("Leverage"), value: symbol.leverage },
    { label: t("Leverage Swing"), value: symbol.leverageSwing },
    { label: t("Commission"), value: symbol.commission },
  ];

  return (
    <div className="flex flex-wrap gap-2 px-4 pb-4 sm:px-5">
      {specs.map((spec) => (
        <StatPill key={spec.label} label={spec.label} value={spec.value} />
      ))}
    </div>
  );
}

function WeeklyScheduleTable({
  schedule,
}: {
  schedule: TradingSymbol["schedule"];
}) {
  const { t } = useTranslation();

  return (
    <div className="gap-2 px-4 pb-4 pt-1 sm:px-5">
      <div className="overflow-hidden rounded-xl bg-gradient-to-r from-[#1CCDE6]/[0.08] via-[#9ED473]/[0.08] to-[#DBD633]/[0.08]">
      <div className="overflow-x-auto hide-scrollbar">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="bg-gradient-to-l from-[#DBD633]/30 via-[#9ED473]/30 to-[#1CCDE6]/30 ">
              {schedule.map((day) => (
                <th key={day.day} className={TABLE_HEAD}>
                  {day.day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {schedule.map((day) => (
                <td key={day.day} className={TABLE_CELL}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        day.open ? "bg-[#99FF48]" : "bg-white/25",
                      )}
                    />
                    <span className={day.open ? "text-white/90" : "text-white/40"}>
                      {day.open ? day.hours : t("Closed")}
                    </span>
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}

function SymbolAccordionItem({
  symbol,
  expanded,
  onToggle,
}: {
  symbol: TradingSymbol;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <article
      className={cn(
        "overflow-hidden rounded-xl transition-all w-full",
        expanded
          ? "btn-gradient-left w-full"
          : "border border-white/10 bg-[#0b0b0b]/80",
      )}
    >
      <div className={cn("overflow-hidden rounded-xl w-full", expanded && "bg-[#0b0b0b]/50")}>
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <SymbolFlag flagCode={symbol.flagCode} />
            <span className="font-neue text-base font-medium text-white sm:text-lg">
              {symbol.pair}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <MarketStatusBadge symbol={symbol} />
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-white" />
            ) : (
              <ChevronDown className="h-4 w-4 text-white/" />
            )}
          </div>
        </button>

        {expanded && (
          <>
            <SymbolSpecBadges symbol={symbol} />
            <WeeklyScheduleTable schedule={symbol.schedule} />
          </>
        )}
      </div>
    </article>
  );
}

export default function SymbolsTradingHoursView() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<SymbolCategory>("Forex");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string>("eur-usd");

  const filteredSymbols = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return TRADING_SYMBOLS.filter((symbol) => {
      const matchesCategory = symbol.category === activeCategory;
      const matchesSearch =
        !query || symbol.pair.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <div className="min-h-full w-full overflow-y-auto hide-scrollbar bg-[#050505] px-4 py-6 text-white sm:px-6 lg:px-8">
      <div className="">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="overflow-x-auto hide-scrollbar">
            <div className="flex w-max overflow-x-auto hide-scrollbar rounded-md border border-white/10 bg-[#121212]">
              {SYMBOL_CATEGORIES.map((category) => (
                <CategoryTab
                  key={category}
                  label={t(category)}
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                />
              ))}
            </div>
          </div>

          <div className="relative w-full lg:max-w-[280px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("Search Symbol")}
              className="support-contact-field w-full rounded-[8px] border border-white/10 bg-[#121212] py-2.5 pl-10 pr-4 font-neue text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
          </div>
        </header>

        <section className="mt-5 flex flex-col gap-3">
          {filteredSymbols.length === 0 ? (
            <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0b0b0b] px-5 py-10 text-center">
              <p className="font-neue text-sm text-white/45">
                {t("No symbols found for this category.")}
              </p>
            </div>
          ) : (
            filteredSymbols.map((symbol) => (
              <SymbolAccordionItem
                key={symbol.id}
                symbol={symbol}
                expanded={expandedId === symbol.id}
                onToggle={() =>
                  setExpandedId((current) =>
                    current === symbol.id ? "" : symbol.id,
                  )
                }
              />
            ))
          )}
        </section>
      </div>
    </div>
  );
}
